import type { ReactNode } from 'react';
import { useEffect } from 'react';

/** A right-side slide-over panel for entity detail (customer / driver). Click
 *  the scrim or press Esc to close. Dependency-free; matches the light admin. */
export function Drawer({
  title,
  subtitle,
  onClose,
  children,
  width = 'default',
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** 'wide' suits multi-column forms like the role permission picker. */
  width?: 'default' | 'wide';
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Scrim */}
      <div className="absolute inset-0 bg-slate-100/20 backdrop-blur-[1px]" onClick={onClose} aria-hidden="true" />
      {/* Panel */}
      <div
        className={`animate-fadeIn relative flex h-full w-full flex-col border-l border-ink-700 bg-ink-900 shadow-overlay ${
          width === 'wide' ? 'max-w-3xl' : 'max-w-xl'
        }`}
      >
        <div className="flex h-16 items-start justify-between gap-3 border-b border-ink-700 px-6">
          <div className="min-w-0 self-center">
            <div className="truncate text-[15px] font-semibold text-slate-100">{title}</div>
            {subtitle ? <div className="truncate text-[13px] text-text-secondary">{subtitle}</div> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="self-center rounded-md px-2 py-1 text-xs text-text-secondary transition-colors hover:bg-hover hover:text-slate-100"
            aria-label="Close"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-5">{children}</div>
      </div>
    </div>
  );
}

/** A labelled key/value row used inside detail drawers. */
export function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-ink-800 py-2">
      <span className="text-[11px] uppercase tracking-wide text-text-muted">{label}</span>
      <span className="min-w-0 truncate text-right text-sm text-slate-200">{value}</span>
    </div>
  );
}
